import { createSvgEl } from '@learnforge/demo-render';
import { type Expr } from '../algebra/expr';
import { type Fraction } from '@learnforge/demo-math/numeric';
import { format } from '../algebra/parse';

export type SignCell = '+' | '-' | '0' | 'undef';

export interface SignChartOptions {
  rowLabels: readonly { factor: Expr; kind: 'factor' | 'asymptote' }[];
  columnBoundaries: readonly Fraction[]; // sorted critical points
  cells: readonly (readonly SignCell[])[]; // rows × (boundaries.length + 1) columns
  productRow: readonly SignCell[];
  width: number;
  height: number;
}

export interface SignChartHandle {
  group: SVGGElement;
  update(opts: Partial<SignChartOptions>): void;
  destroy(): void;
}

function getCellClass(cell: SignCell): string {
  switch (cell) {
    case '+':
      return 'alg-sign-pos';
    case '-':
      return 'alg-sign-neg';
    case '0':
      return 'alg-sign-zero';
    case 'undef':
      return 'alg-sign-undef';
  }
}

export function createSignChart(
  parent: SVGSVGElement | SVGGElement,
  opts: SignChartOptions,
): SignChartHandle {
  const group = createSvgEl('g');
  parent.appendChild(group);

  const totalRows = opts.rowLabels.length + 1;
  const totalCols = opts.columnBoundaries.length + 1;
  const labelWidth = 100;
  const cellWidth = (opts.width - labelWidth) / totalCols;
  const cellHeight = opts.height / totalRows;

  const render = (options: SignChartOptions): void => {
    while (group.firstChild) {
      group.removeChild(group.firstChild);
    }

    // Render factor rows
    for (let rowIdx = 0; rowIdx < options.rowLabels.length; rowIdx += 1) {
      const rowLabel = options.rowLabels[rowIdx]!;
      const rowG = createSvgEl('g', {
        'data-role': 'factor-row',
        'data-kind': rowLabel.kind,
      });
      group.appendChild(rowG);

      const y = rowIdx * cellHeight;

      // Render label
      const labelText = createSvgEl('text', {
        x: 10,
        y: y + cellHeight / 2 + 5,
        'font-size': 12,
        'font-family': 'sans-serif',
        class: 'alg-text',
      });
      labelText.textContent = format(rowLabel.factor);
      rowG.appendChild(labelText);

      // Render cells
      const row = options.cells[rowIdx];
      if (row) {
        for (let colIdx = 0; colIdx < row.length; colIdx += 1) {
          const cell = row[colIdx];
          if (!cell) break;

          const x = labelWidth + colIdx * cellWidth;
          const cellRect = createSvgEl('rect', {
            x,
            y,
            width: cellWidth,
            height: cellHeight,
            class: getCellClass(cell),
            stroke: 'none',
            'data-sign': cell,
          });
          rowG.appendChild(cellRect);

          const cellText = createSvgEl('text', {
            x: x + cellWidth / 2,
            y: y + cellHeight / 2 + 5,
            'text-anchor': 'middle',
            'font-size': 14,
            'font-family': 'sans-serif',
            'font-weight': 'bold',
            class: 'alg-glyph',
          });
          cellText.textContent = cell;
          rowG.appendChild(cellText);
        }
      }
    }

    // Render product row
    const productY = options.rowLabels.length * cellHeight;
    const productRowG = createSvgEl('g', {
      'data-role': 'product-row',
    });
    group.appendChild(productRowG);

    const productLabelText = createSvgEl('text', {
      x: 10,
      y: productY + cellHeight / 2 + 5,
      'font-size': 12,
      'font-family': 'sans-serif',
      'font-weight': 'bold',
      class: 'alg-text',
    });
    productLabelText.textContent = 'Product';
    productRowG.appendChild(productLabelText);

    for (let colIdx = 0; colIdx < options.productRow.length; colIdx += 1) {
      const cell = options.productRow[colIdx];
      if (!cell) break;

      const x = labelWidth + colIdx * cellWidth;
      const cellRect = createSvgEl('rect', {
        x,
        y: productY,
        width: cellWidth,
        height: cellHeight,
        class: getCellClass(cell),
        stroke: 'none',
        'data-sign': cell,
      });
      productRowG.appendChild(cellRect);

      const cellText = createSvgEl('text', {
        x: x + cellWidth / 2,
        y: productY + cellHeight / 2 + 5,
        'text-anchor': 'middle',
        'font-size': 14,
        'font-family': 'sans-serif',
        'font-weight': 'bold',
        class: 'alg-glyph',
      });
      cellText.textContent = cell;
      productRowG.appendChild(cellText);
    }

    // Render boundary labels
    for (let colIdx = 0; colIdx < options.columnBoundaries.length; colIdx += 1) {
      const boundary = options.columnBoundaries[colIdx]!;
      const x = labelWidth + (colIdx + 1) * cellWidth;
      const y = options.height + 20;

      const boundaryText = createSvgEl('text', {
        x,
        y,
        'text-anchor': 'middle',
        'font-size': 11,
        'font-family': 'monospace',
        class: 'alg-text-mute',
      });
      boundaryText.textContent = boundary.toString();
      group.appendChild(boundaryText);

      // Vertical line at boundary
      const line = createSvgEl('line', {
        x1: x,
        y1: 0,
        x2: x,
        y2: options.height,
        class: 'alg-line',
        'stroke-dasharray': '4,4',
      });
      group.insertBefore(line, group.firstChild);
    }
  };

  render(opts);

  return {
    group,
    update(newOpts: Partial<SignChartOptions>): void {
      const merged = { ...opts, ...newOpts };
      render(merged);
    },
    destroy(): void {
      parent.removeChild(group);
    },
  };
}
